import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import {
    HydraTable,
    Heading,
    TextFilter,
    DateRangeFilter,
    StyledCheckbox,
    TimeAgo,
} from '@peracto/peracto-ui'
import { Helmet } from 'react-helmet'

import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'

import ContentImportButton from '../ContentTransfer/ContentImportButton'

const ArticlesList = () => {
    // Default visible columns - key = column accessor
    const [selectedColumns, onSelectColumn] = useState({
        modified: true,
        modifiedBy: true,
        locale: true,
    })

    const { client } = useClient()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const { article } = config.get('features', {})

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => [
        {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
                <StyledCheckbox
                    {...row.getToggleRowSelectedProps()}
                    data-testid={`row-${row.index}-checkbox`}
                />
            ),
            selectable: false,
        },
        {
            Header: 'Heading',
            accessor: 'name',
            sortType: 'basic',
            Filter: props => <TextFilter label="Heading" {...props} />,
            Cell: row => (
                <Link to={`/content/edit/article/${row.row.original.originId}`}>
                    {row.cell.value}
                </Link>
            ),
            selectable: false,
        },
        {
            Header: 'Modified By',
            accessor: row => {
                if (row.modifiedBy && row.modifiedBy.hasOwnProperty('firstName')) {
                    return `${row.modifiedBy.firstName} ${row.modifiedBy.lastName}`
                } else {
                    return 'n/a'
                }
            },
            id: 'modifiedBy',
            show: selectedColumns['modifiedBy'],
            defaultCanSort: false,
            disableSortBy: true,
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedAt',
            sortType: 'basic',
            Filter: props => <DateRangeFilter label="Last Modified" {...props} />,
            Cell: ({ cell: { value } }) =>
                value ? <TimeAgo title={formatDate(new Date(value))} date={value} /> : '-',
            show: selectedColumns['modifiedAt'],
            selectable: true,
        },
        {
            Header: 'Locale',
            accessor: 'locale',
            sortType: 'basic',
            Filter: props => <TextFilter label="Locale" {...props} />,
            show: selectedColumns['locale'],
            selectable: true,
        },
    ])

    const onFetchData = options => client(GET_LIST, 'contents', options)

    return (
        <>
            <Helmet>
                <title>Articles | Peracto</title>
            </Helmet>
            <Heading name="Articles">
                <div className="!flex !items-center !ml-2">
                    <ContentImportButton type="article" />
                    <Link
                        className="btn btn-primary"
                        to="/content/edit/article/"
                        data-testid="create-new-article"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                        Create New Article
                    </Link>
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                alwaysFilter={
                                    article?.blogToggle
                                        ? { type: ['article', 'blog'] }
                                        : { type: 'article' }
                                }
                                allowDuplicate
                                defaultFilters={{
                                    name: '',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ArticlesList
